/**
 * FixedBills — monthly bill list with mark-paid / un-pay support.
 * When a credit card bill is paid it applies monthly interest and subtracts
 * the payment from the running card balance.
 */
import React, { useState } from 'react';
import { fc, isNowMo } from '../../utils';

export function FixedBills({
  activeBills,
  paidBills,
  cards,
  now,
  viewY,
  viewM,
  k,
  updateState,
  showToast,
  triggerConfetti,
  bankBalance = 0,
  bankBalanceDate: _bankBalanceDate = null, // eslint-disable-line no-unused-vars
}) {
  const [pendingBill, setPendingBill] = useState(null);

  // Auto-adjust bank balance when bills are paid/unpaid (current month only)
  const todayStr = now.toISOString().slice(0, 10);
  const bankAdjust = (delta) =>
    bankBalance > 0 && isNowMo(viewY, viewM)
      ? { bankBalance: Math.max(0, Math.round((bankBalance + delta) * 100) / 100), bankBalanceDate: todayStr }
      : {};

  const handleConfirm = (bid, actualAmt, billName) => {
    const amt = parseFloat(actualAmt) || 0;
    const updated = { ...paidBills };
    if (!updated[k]) updated[k] = {};
    updated[k] = { ...updated[k], [bid]: amt > 0 ? amt : true };
    setPendingBill(null);
    const card = (cards || []).find((c) => c.id === bid);
    const payAmt = amt > 0 ? amt : card ? card.min : (activeBills.find((b) => b.id === bid)?.amt || 0);
    const bankUpd = bankAdjust(-payAmt);
    const bankMsg = bankBalance > 0 && isNowMo(viewY, viewM) ? ' · 🏦 balance updated' : '';
    if (card) {
      const interest = card.bal * (card.apr / 100 / 12);
      const payment = amt > 0 ? amt : card.min;
      const newBal = Math.max(0, Math.round((card.bal + interest - payment) * 100) / 100);
      updateState({ paidBills: updated, cards: (cards || []).map((c) => (c.id === bid ? { ...c, bal: newBal } : c)), ...bankUpd });
      if (newBal === 0) { triggerConfetti?.(); showToast('🎉 Debt paid off!'); }
      else showToast(`✓ ${billName} paid${bankMsg}`);
    } else {
      updateState({ paidBills: updated, ...bankUpd });
      showToast(`✓ ${billName} paid${bankMsg}`);
    }
  };

  const handleUnpay = (bid, billName) => {
    const storedAmt = (paidBills[k] || {})[bid];
    const updated = { ...paidBills };
    if (!updated[k]) updated[k] = {};
    updated[k] = { ...updated[k], [bid]: false };
    const card = (cards || []).find((c) => c.id === bid);
    const prevPaid = typeof storedAmt === 'number' ? storedAmt : card ? card.min : (activeBills.find((b) => b.id === bid)?.amt || 0);
    const bankUpd = bankAdjust(prevPaid); // add back what was deducted
    if (card) {
      const newBal = Math.round((card.bal + prevPaid) * 100) / 100;
      updateState({ paidBills: updated, cards: (cards || []).map((c) => (c.id === bid ? { ...c, bal: newBal } : c)), ...bankUpd });
    } else {
      updateState({ paidBills: updated, ...bankUpd });
    }
    showToast(`↩ ${billName} marked unpaid`);
  };

  const pd = paidBills[k] || {};

  return (
    <div className="card cp">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div className="st" style={{ marginBottom: 0 }}>✅ Fixed Bills</div>
      </div>
      <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 10 }}>
        Sorted by due date — tap to mark paid.
      </div>

      {!activeBills.length && (
        <div className="em">No bills set up yet — add them in 🗺️ Plan → Bills</div>
      )}

      {activeBills.map((b) => {
        const due = new Date(viewY, viewM, b.due);
        const isPaid = !!pd[b.id];
        const isOv = !isPaid && due < now && isNowMo(viewY, viewM);
        const isPending = pendingBill?.id === b.id;
        const actualAmt = typeof pd[b.id] === 'number' ? pd[b.id] : null;

        return (
          <div key={b.id} className="br" style={{ opacity: isPaid && !isPending ? 0.6 : 1, flexWrap: 'wrap' }}>
            <button
              className={'bt' + (isPaid ? ' ck' : '')}
              onClick={() => isPaid ? handleUnpay(b.id, b.name) : setPendingBill({ id: b.id, val: String(b.amt) })}
            >
              {isPaid ? '✓' : ''}
            </button>
            <div className="dot" style={{ background: b.col }} />
            <div className="bi">
              <div className={'bn' + (isPaid ? ' sk' : '')} style={{ color: isOv ? 'var(--red)' : '' }}>{b.name}</div>
              <div className={'bd' + (isOv ? ' ov' : '')}>
                Due {due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{isOv ? ' — overdue' : ''}
              </div>
            </div>
            <div className="bam" style={{ color: isPaid ? 'var(--mut)' : isOv ? 'var(--red)' : b.col }}>
              {actualAmt !== null && actualAmt !== b.amt ? (
                <><span style={{ textDecoration: 'line-through', fontSize: 11, opacity: 0.5 }}>{fc(b.amt)}</span> {fc(actualAmt)}</>
              ) : fc(b.amt)}
            </div>

            {isPending && (
              <div style={{ width: '100%', display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', paddingLeft: 32 }}>
                <span style={{ fontSize: 12, color: 'var(--mut)' }}>Actual amount paid: $</span>
                <input
                  type="number" min="0" step="0.01"
                  value={pendingBill.val}
                  onChange={(e) => setPendingBill((p) => ({ ...p, val: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirm(b.id, pendingBill.val, b.name)}
                  style={{ width: 90, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 13 }}
                  autoFocus
                />
                <button
                  onClick={() => handleConfirm(b.id, pendingBill.val, b.name)}
                  style={{ padding: '4px 12px', borderRadius: 6, background: 'var(--ylw)', color: '#000', fontWeight: 700, fontSize: 12, border: 'none', cursor: 'pointer' }}
                >
                  ✓ Paid
                </button>
                <button
                  onClick={() => setPendingBill(null)}
                  style={{ padding: '4px 10px', borderRadius: 6, background: 'none', border: '1px solid var(--bdr)', color: 'var(--mut)', fontSize: 12, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
